import {getElementByElementOrId, identifyElement} from "../helpers/element";
import {MixinStateMachine} from "./mixins/stateable";
import {MixinEvents} from "./mixins/eventable";
import {MixinHtmlClassState} from "./mixins/html_class_state";
import {MixinDatasetConfigurable} from "./mixins/dataset_configurable";
import {MixinUrlConfigurable} from "./mixins/url_configurable";
import {ComponentConfig, InitializedClassModifier} from "./component_config";
import {markElementAsComponentized, unmarkElementAsComponentized} from "./component_constructor";


// Base component class takes a props object.
// Generally includes a `element` property and a `config` property.
// The config property should include a `baseHtmlClass` and `selector`.
export default class Component {

    // Get the standard configuration properties that are used by the component.
    // Subclasses of Component should override `static get config()` with a reference to their own configuration.
    static get config() {
        return ComponentConfig;
    }

    // Convenience function to create instances of Component or Subclasses (eg. MyComponent) from elements in a DOM.
    // Call it from inside a Component subclass like: `this.myComponents = MyComponent.constructComponents({ parentInstance: this });` and you'll get instantiated components of that type in the DOM.
    // @param options = { parentInstance: this, scopeElement: document.body }
    // The `parentInstance` property is an optional class instance (often another component). If provided, it will provide the scope within the DOM for finding component elements. It will also be used when calling the constructor of the component as the second argument.
    // The `scopeElement` property is an optional element. If provided, it will provide the scope within the DOM for finding component elements.
    // If neither `parentInstance` or `scopeElement` are provided, the entire document body will be used as the scope.
    // @returns An array of instantiated components.
    static constructComponents(options = {}) {
        if (!(typeof (Object.getOwnPropertyDescriptor(this, 'config').get) === 'function')) throw new Error('Component.getComponents() componentClass.config is not a getter function. Define a static getter function on the component class that returns the default component configuration');
        const parentInstance = options.parentInstance;
        const scopeElement = parentInstance?.element || options.scopeElement || document.body;
        const elements = Array.from(scopeElement.querySelectorAll(this.config.selector));
        const instances = elements.map((element) => {
            if (!!parentInstance) return new this(element, parentInstance);
            return new this(element);
        });

        // Children constructed for a parent Component are adopted by it, so that destroying the
        // parent destroys them too. Parent-constructed children never enter the ComponentRegistry,
        // so the reaper cannot find them — the parent is their only route to a destroy().
        if (parentInstance && typeof parentInstance.adoptChildComponents === 'function') parentInstance.adoptChildComponents(instances);

        return instances;
    }

    static createElement(baseHtmlClass, options = {}) {
        const tagName = options.tagName || 'div';
        let element = document.createElement(tagName);
        const htmlClass = `${baseHtmlClass}`;
        element.setAttribute("class", htmlClass);
        this.content = null;
        return element;
    }

    constructor(props = {}) {
        this.element = (props.hasOwnProperty('element') ? getElementByElementOrId(props.element) : null);
        this.id = identifyElement(this.element);

        // Lifecycle (see `destroy`). `teardowns` are registered by subclasses via `onDestroy`;
        // `childComponents` are registered by the parent via `adoptChildComponents`.
        this.isDestroyed = false;
        this.teardowns = [];
        this.childComponents = [];

        const config = (props.hasOwnProperty('config') ? props.config : {});
        this.configure(config);

        MixinHtmlClassState(this); // Add the HTML class state management methods.

        MixinEvents(this); // Add the event management methods and set `this.events`.

        MixinDatasetConfigurable(this); // Add methods to gather configuration key/value pairs from the elements dataset attributes.

        MixinUrlConfigurable(this); // Add methods to assist with extracting values from URLs.

        this.render = this.render || function () {
        }; // Placeholder render method. Every component has a render method which Subclasses should override.

        MixinStateMachine(this); // Add the state machine management methods to `this`.
        if (props.hasOwnProperty('stateMachine')) this.setStateMachine(props.stateMachine); // Set `this.stateMachine`
        this.handleStateChange = () => this.render(); // The state machine's handler `handleStateChange` will trigger the component's `render` method.

        this.storeComponentOnElement();
        this.markElementComponentized(this.componentTypeKey);
    }

    // Set important component properties from any supplied config.
    configure(config) {
        this.config = config;
        this.componentTypeKey = (config.hasOwnProperty('componentTypeKey') ? config.componentTypeKey : null);
        this.baseHtmlClass = (config.hasOwnProperty('baseHtmlClass') ? config.baseHtmlClass : null);
        this.selector = (config.hasOwnProperty('selector') ? config.selector : null);
    }

    // Register a teardown function to run when this component is destroyed.
    //
    // The rule: anything a component attaches to something which OUTLIVES its element must be
    // registered here — a subscription to a singleton (eg. the SessionStateDetector), a listener
    // on `document` / `window`, a `setInterval` / `setTimeout`. Otherwise it keeps running, and
    // keeps the component (and its whole DOM subtree) alive, long after the element is gone.
    //
    // Listeners on the component's OWN element need nothing — they are discarded with the
    // element. The state machine subscription needs nothing — `destroy` detaches it.
    onDestroy(teardownFn) {
        if (typeof teardownFn !== 'function') return;
        this.teardowns.push(teardownFn);
    }

    // Adopt components constructed by this component, so that destroying this component destroys
    // them. Accepts components as arguments or as an array. Returns what it was given — a single
    // component when given one, otherwise an array — so it reads naturally inline:
    //
    //   this.panels = this.adoptChildComponents(this.acceptForm = new AcceptForm(...), ...);
    //   this.button = this.adoptChildComponents(new Button(...));
    //
    // `Component.constructComponents({parentInstance})` adopts automatically.
    adoptChildComponents(...components) {
        const adopted = components.flat().filter((component) => component && typeof component.destroy === 'function');
        this.childComponents.push(...adopted);
        if (components.length === 1) return components[0];
        return components;
    }

    // Undo the component: destroy adopted children, run the subclass's registered teardowns,
    // detach from the state machine, deregister from the ComponentRegistry, and un-mark the
    // element. Idempotent.
    //
    // Called automatically when the component's element is removed from the DOM (see the reaper
    // in component_constructor.js), so most components never call it. It is safe to call by hand
    // to replace a component on an element which stays in the page — un-marking the element makes
    // it eligible for re-initialization.
    //
    // Children and teardowns are run in reverse order of registration (LIFO), the usual unwinding
    // order: the last thing set up is the first torn down. Cleanup is best-effort — a throwing
    // teardown is logged, and does not prevent the rest of the component (or, in a reaper sweep,
    // the other components) from being destroyed.
    destroy() {
        if (this.isDestroyed) return;
        this.isDestroyed = true;

        const children = [...this.childComponents].reverse();
        this.childComponents = [];
        children.forEach((child) => this.runCleanup(() => child.destroy(), 'child component destroy'));

        const teardowns = [...this.teardowns].reverse();
        this.teardowns = [];
        teardowns.forEach((teardown) => this.runCleanup(teardown, 'onDestroy teardown'));

        this.unsetStateMachine();

        if (this.componentRegistry) this.componentRegistry.removeComponent(this.componentTypeKey, this);

        this.unmarkElement();
    }

    runCleanup(fn, description) {
        try {
            fn();
        } catch (e) {
            console.error(`[Component] ${this.componentTypeKey} destroy() — ${description} threw:`, e, this);
        }
    }

    // Reverse the element bookkeeping done at construction, so the element no longer refers back
    // to this component and no longer counts as componentized with this component's type.
    unmarkElement() {
        if (!this.element) return;

        const components = this.element[Component.config.elementComponentsKey];
        if (Array.isArray(components)) {
            const index = components.indexOf(this);
            if (index !== -1) components.splice(index, 1);
        }

        unmarkElementAsComponentized(this.element, this.componentTypeKey);

        const initializedHtmlClass = this.getInitializedHtmlClass();
        if (initializedHtmlClass) this.element.classList.remove(initializedHtmlClass);
    }

    // The loader sets `componentInitializer` on the instances it constructs; a component
    // constructed by a parent has none, so derive the class from the config instead.
    getInitializedHtmlClass() {
        if (this.componentInitializer?.initializedHtmlClass) return this.componentInitializer.initializedHtmlClass;
        if (!this.baseHtmlClass) return null;
        return `${this.baseHtmlClass}--${InitializedClassModifier}`;
    }

    storeComponentOnElement() {
        if (!this.element) return;
        if (typeof this.element[Component.config.elementComponentsKey] === "undefined") this.element[Component.config.elementComponentsKey] = [];
        this.element[Component.config.elementComponentsKey].push(this);
    }

    // Sign the component's componentTypeKey string to the element's "componentizedWith" array.
    // The 'ComponentConstructor.isElementComponentized` method checks this property to see if an element is already inited with any specific type of component.
    markElementComponentized(componentTypeKey) {
        if (typeof componentTypeKey !== 'string') return;
        markElementAsComponentized(this.element, componentTypeKey);
    }

}
